import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'

function formatDateTime(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return d.toLocaleString('es-AR', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZone: 'America/Argentina/Buenos_Aires'
  })
}

export default function Retiros({ usuario }) {
  const { usuario: authUsuario } = useAuth()
  const isAdmin = usuario?.rol === 'admin'
  const edificioId = isAdmin ? null : (authUsuario?.edificios?.id || null)

  const [tab, setTab] = useState('pendientes') // pendientes | retirados
  const [retiros, setRetiros] = useState([])
  const [oficinas, setOficinas] = useState([])
  const [contactos, setContactos] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null) // null | 'nuevo' | { tipo: 'entregar', retiro }
  const [saving, setSaving] = useState(false)
  const [msgOk, setMsgOk] = useState('')

  // Form nuevo retiro
  const [form, setForm] = useState({
    oficina_id: '',
    contacto_id: '',
    persona_deja_nombre: '',
    descripcion: '',
    quien_busca: '',
    observaciones: '',
    usar_otro_contacto: false,
  })

  // Form entregar
  const [formEntregar, setFormEntregar] = useState({ retirado_por_nombre: '' })

  const cargar = useCallback(async () => {
    setLoading(true)
    let query = supabase
      .from('retiros')
      .select('*, oficinas(numero, titular), edificios(nombre)')
      .order('created_at', { ascending: false })

    if (!isAdmin && edificioId) {
      query = query.eq('edificio_id', edificioId)
    }
    if (tab === 'pendientes') query = query.eq('estado', 'pendiente')
    if (tab === 'retirados') query = query.eq('estado', 'retirado')

    const { data } = await query
    setRetiros(data || [])
    setLoading(false)
  }, [tab, isAdmin, edificioId])

  useEffect(() => {
    const fetchOficinas = async () => {
      let q = supabase.from('oficinas').select('id, numero, titular, edificio_id').eq('activa', true).order('numero')
      if (!isAdmin && edificioId) q = q.eq('edificio_id', edificioId)
      const { data } = await q
      setOficinas(data || [])
    }
    fetchOficinas()
  }, [isAdmin, edificioId])

  useEffect(() => {
    cargar()
  }, [cargar])

  // Cargar contactos cuando cambia la oficina seleccionada
  useEffect(() => {
    if (!form.oficina_id) {
      setContactos([])
      return
    }
    const fetchContactos = async () => {
      const { data, error } = await supabase
        .from('contactos_oficina')
        .select('id, nombre, apellido, email, telefono')
        .eq('oficina_id', form.oficina_id)
        .order('nombre')
      console.log('Contactos cargados:', data, 'error:', error, 'oficina_id:', form.oficina_id)
      setContactos(data || [])
    }
    fetchContactos()
  }, [form.oficina_id])

  const abrirNuevo = () => {
    setForm({
      oficina_id: '',
      contacto_id: '',
      persona_deja_nombre: '',
      descripcion: '',
      quien_busca: '',
      observaciones: '',
      usar_otro_contacto: false,
    })
    setModal('nuevo')
  }

  const guardarRetiro = async () => {
    if (!form.oficina_id || !form.descripcion.trim() || !form.quien_busca.trim()) return
    setSaving(true)

    const oficina = oficinas.find(o => o.id === form.oficina_id)
    let personaNombre = ''
    let personaEmail = ''
    let contactoId = null

    if (form.usar_otro_contacto) {
      personaNombre = form.persona_deja_nombre.trim()
    } else if (form.contacto_id) {
      const c = contactos.find(c => c.id === form.contacto_id)
      if (c) {
        personaNombre = `${c.nombre} ${c.apellido || ''}`.trim()
        personaEmail = c.email || ''
        contactoId = c.id
      }
    }

    const { error } = await supabase.from('retiros').insert({
      edificio_id: oficina?.edificio_id,
      oficina_id: form.oficina_id,
      contacto_id: contactoId,
      persona_deja_nombre: personaNombre,
      persona_deja_email: personaEmail,
      descripcion: form.descripcion.trim(),
      quien_busca: form.quien_busca.trim() || null,
      observaciones: form.observaciones.trim() || null,
      usuario_id: usuario.id,
    })

    if (!error) {
      setMsgOk('✅ Retiro registrado')
      setTimeout(() => setMsgOk(''), 3000)
      setModal(null)
      cargar()
    }
    setSaving(false)
  }

  const abrirEntregar = (retiro) => {
    setFormEntregar({ retirado_por_nombre: retiro.quien_busca || '' })
    setModal({ tipo: 'entregar', retiro })
  }

  const confirmarEntrega = async () => {
    if (!formEntregar.retirado_por_nombre.trim()) return
    setSaving(true)
    const retiro = modal.retiro

    const { error } = await supabase
      .from('retiros')
      .update({
        estado: 'retirado',
        retirado_por_nombre: formEntregar.retirado_por_nombre.trim(),
        fecha_retiro: new Date().toISOString(),
      })
      .eq('id', retiro.id)

    if (!error) {
      // Enviar email de notificación
      if (retiro.persona_deja_email) {
        await fetch('/api/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tipo: 'retiro_entregado',
            destinatario_email: retiro.persona_deja_email,
            oficina: retiro.oficinas?.numero || '',
            oficina_titular: retiro.oficinas?.titular || '',
            edificio: retiro.edificios?.nombre || '',
            datos: {
              descripcion: retiro.descripcion,
              retirado_por: formEntregar.retirado_por_nombre.trim(),
              persona_deja: retiro.persona_deja_nombre,
            }
          })
        }).catch(() => {})
      }

      setMsgOk('✅ Retiro entregado' + (retiro.persona_deja_email ? ' y notificado' : ''))
      setTimeout(() => setMsgOk(''), 3000)
      setModal(null)
      cargar()
    }
    setSaving(false)
  }

  return (
    <div style={{ padding: '24px 16px', maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, color: 'var(--gold)', letterSpacing: 1 }}>Retiros</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text3)' }}>
            {retiros.filter(r => r.estado === 'pendiente').length} {retiros.filter(r => r.estado === 'pendiente').length === 1 ? 'envío pendiente' : 'envíos pendientes'}
          </p>
        </div>
        <button className="btn btn-gold" onClick={abrirNuevo}>+ Nuevo retiro</button>
      </div>

      {msgOk && (
        <div style={{ background: '#1a2e1a', border: '1px solid var(--green)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: 'var(--green)', fontSize: 14 }}>
          {msgOk}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button className={`btn btn-sm ${tab === 'pendientes' ? 'btn-gold' : 'btn-ghost'}`} onClick={() => setTab('pendientes')}>
          Pendientes
        </button>
        <button className={`btn btn-sm ${tab === 'retirados' ? 'btn-gold' : 'btn-ghost'}`} onClick={() => setTab('retirados')}>
          Retirados
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--text3)' }}>Cargando...</div>
      ) : retiros.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--text3)' }}>
          {tab === 'pendientes' ? 'No hay retiros pendientes' : 'No hay retiros entregados'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {retiros.map(r => (
            <div key={r.id} style={{
              background: 'var(--card)', borderRadius: 10, padding: '14px 18px',
              border: '1px solid #333', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap'
            }}>
              <div style={{ fontSize: 22 }}>📦</div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text1)' }}>
                    {r.descripcion}
                  </span>
                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, background: 'var(--gold)22', color: 'var(--gold)', fontFamily: 'DM Mono, monospace' }}>
                    Of. {r.oficinas?.numero}
                  </span>
                  {isAdmin && (
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, background: '#2a2a2a', color: 'var(--text3)', fontFamily: 'DM Mono, monospace' }}>
                      {r.edificios?.nombre}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4, fontFamily: 'DM Mono, monospace' }}>
                  Dejado por: <span style={{ color: 'var(--text2)' }}>{r.persona_deja_nombre || '—'}</span>
                  {' · '}
                  {formatDateTime(r.created_at)}
                </div>
                {r.estado === 'retirado' && (
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2, fontFamily: 'DM Mono, monospace' }}>
                    Entregado: {formatDateTime(r.fecha_retiro)}
                  </div>
                )}
                {r.quien_busca && r.estado === 'pendiente' && (
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2, fontFamily: 'DM Mono, monospace' }}>
                    Lo busca: <span style={{ color: 'var(--text2)' }}>{r.quien_busca}</span>
                  </div>
                )}
                {r.estado === 'retirado' && (
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2, fontFamily: 'DM Mono, monospace' }}>
                    Retirado por: <span style={{ color: 'var(--text2)' }}>{r.retirado_por_nombre}</span>
                  </div>
                )}
                {r.observaciones && (
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2, fontStyle: 'italic' }}>
                    {r.observaciones}
                  </div>
                )}
              </div>
              {r.estado === 'pendiente' && (
                <button className="btn btn-gold btn-sm" onClick={() => abrirEntregar(r)}>
                  Entregar ✓
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ─── MODAL NUEVO RETIRO ─── */}
      {modal === 'nuevo' && (
        <div style={{ position: 'fixed', inset: 0, background: '#000a', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setModal(null)}>
          <div style={{ background: 'var(--bg2)', borderRadius: 14, padding: 28, width: '100%', maxWidth: 480, border: '1px solid var(--border)', maxHeight: '90vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 20px', color: 'var(--gold)', fontSize: 16 }}>+ Nuevo retiro</h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 6 }}>Oficina *</label>
                <select className="input" value={form.oficina_id} onChange={e => setForm(f => ({ ...f, oficina_id: e.target.value, contacto_id: '', usar_otro_contacto: false }))}>
                  <option value="">Seleccioná una oficina</option>
                  {oficinas.map(o => <option key={o.id} value={o.id}>{o.numero} — {o.titular}</option>)}
                </select>
              </div>

              {form.oficina_id && (
                <div>
                  <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 6 }}>Persona que deja</label>
                  {!form.usar_otro_contacto ? (
                    <>
                      <select className="input" value={form.contacto_id} onChange={e => {
                        console.log('Select onChange:', e.target.value)
                        if (e.target.value === '__otro__') {
                          setForm(f => ({ ...f, usar_otro_contacto: true, contacto_id: '' }))
                        } else {
                          setForm(f => ({ ...f, contacto_id: e.target.value }))
                        }
                      }}>
                        <option value="">Seleccioná un contacto</option>
                        {contactos.map(c => {
                          console.log('Rendering contacto option:', c)
                          return <option key={c.id} value={c.id}>{c.nombre} {c.apellido || ''}</option>
                        })}
                        <option value="__otro__">+ Otra persona</option>
                      </select>
                      <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--text3)' }}>
                        {contactos.length === 0 ? 'Esta oficina no tiene contactos cargados. Elegí "Otra persona".' : `${contactos.length} contacto${contactos.length > 1 ? 's' : ''} disponible${contactos.length > 1 ? 's' : ''}`}
                      </p>
                    </>
                  ) : (
                    <>
                      <input className="input" placeholder="Nombre de la persona" value={form.persona_deja_nombre}
                        onChange={e => setForm(f => ({ ...f, persona_deja_nombre: e.target.value }))} />
                      <button className="btn btn-ghost btn-sm" style={{ marginTop: 6, fontSize: 11 }}
                        onClick={() => setForm(f => ({ ...f, usar_otro_contacto: false, persona_deja_nombre: '' }))}>
                        ← Usar contacto de la oficina
                      </button>
                    </>
                  )}
                </div>
              )}

              <div>
                <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 6 }}>Descripción *</label>
                <input className="input" placeholder="Sobre A4, caja chica, llaves..." value={form.descripcion}
                  onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} />
              </div>

              <div>
                <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 6 }}>Quién lo viene a buscar *</label>
                <input className="input" placeholder="Juan de Movistar, mensajería..." value={form.quien_busca}
                  onChange={e => setForm(f => ({ ...f, quien_busca: e.target.value }))} />
              </div>

              <div>
                <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 6 }}>Observaciones (opcional)</label>
                <textarea className="input" rows={2} value={form.observaciones}
                  onChange={e => setForm(f => ({ ...f, observaciones: e.target.value }))}
                  style={{ resize: 'vertical' }} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancelar</button>
              <button className="btn btn-gold" onClick={guardarRetiro}
                disabled={saving || !form.oficina_id || !form.descripcion.trim() || !form.quien_busca.trim()}>
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL ENTREGAR ─── */}
      {modal?.tipo === 'entregar' && (
        <div style={{ position: 'fixed', inset: 0, background: '#000a', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setModal(null)}>
          <div style={{ background: 'var(--bg2)', borderRadius: 14, padding: 28, width: '100%', maxWidth: 380, border: '1px solid var(--gold)' }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 6px', color: 'var(--gold)', fontSize: 16 }}>Entregar retiro</h3>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--text3)' }}>
              <strong style={{ color: 'var(--text1)' }}>{modal.retiro.descripcion}</strong><br />
              Of. {modal.retiro.oficinas?.numero} · Dejado por {modal.retiro.persona_deja_nombre}
            </p>

            <div>
              <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 6 }}>Nombre de quien retira *</label>
              <input className="input" placeholder="Nombre y apellido" value={formEntregar.retirado_por_nombre}
                onChange={e => setFormEntregar({ retirado_por_nombre: e.target.value })}
                autoFocus />
            </div>

            {modal.retiro.persona_deja_email && (
              <p style={{ margin: '12px 0 0', fontSize: 11, color: 'var(--text3)', fontStyle: 'italic' }}>
                Se enviará una notificación a {modal.retiro.persona_deja_email}
              </p>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancelar</button>
              <button className="btn btn-gold" onClick={confirmarEntrega}
                disabled={saving || !formEntregar.retirado_por_nombre.trim()}>
                {saving ? 'Guardando...' : '✓ Confirmar entrega'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
